import React from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import { FilePondErrorDescription, FilePondFile, FilePondServerConfigProps } from 'filepond';
import FilePondImagePreview from 'filepond-plugin-image-preview';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import { InputWrapper, Text } from '@mantine/core';
import { useActions, useAppState } from 'src/state';
import { AppState } from 'src/state/_types';
import { Document_Role_Enum, Document_Type_Enum } from 'src/state/gql/_types';
import { AssetUploadProps, parseHeaders } from './AssetUpload';

registerPlugin(FilePondImagePreview);

const UPLOAD_API_URL = import.meta.env.VITE_UPLOAD_API_URL || ''
const APP_NAME = import.meta.env.VITE_APP_NAME || ''

const generateServerConfig = (auth: AppState['auth'], profile: AppState['profile']) => {

    const assetPath = [
        APP_NAME.toLowerCase(),
        profile?.tenant_id || '',
        'guideline-documents',
    ].filter(Boolean).join('/')

    return {
        process: (fieldName: string, file: Blob & { name: string }, _metadata: unknown, load: (response: unknown) => void, error: (message: string) => void, progress: (computable: boolean, loaded: number, total: number) => void, abort: () => void) => {

            const formData = new FormData();
            const fileToUpload = file instanceof File ? file : new File([file], file.name);
            formData.append(fieldName, fileToUpload, file.name);
            formData.append("user_id", auth.user_id || '');
            formData.append("tenant_id", profile?.tenant_id || '');
            formData.append("asset_path", assetPath);

            const request = new XMLHttpRequest();
            request.open('POST', `${UPLOAD_API_URL}/process`);

            request.upload.onprogress = (e) => {
                progress(e.lengthComputable, e.loaded, e.total);
            };

            request.onload = function () {
                if (request.status >= 200 && request.status < 300) {
                    const resultData = JSON.parse(request.responseText);
                    load(resultData.url)
                } else {
                    error('Error Processing Upload');
                }
            };

            request.send(formData);

            return {
                abort: () => {
                    request.abort();
                    abort();
                },
            };

        },
        // revert: (source: string, load: () => void, error: (message: string) => void) => {

        //     fetch(`${UPLOAD_API_URL}/revert`, {
        //         method: 'POST',
        //         headers: { 'Content-Type': 'application/json' },
        //         body: JSON.stringify({
        //             url: source,
        //             user_id: auth.user_id,
        //             tenant_id: profile?.tenant_id
        //         }),
        //     })
        //         .then(() => load())
        //         .catch((e) => error(e));

        // },
        load: (source, load, error, progress, abort, headers) => {

            let controller = new AbortController();

            let requestPayload = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    url: source
                }),
                signal: controller.signal
            }

            fetch(`${UPLOAD_API_URL}/load`, requestPayload)
                .then(response => {
                    if (response.body) {

                        let headersString = parseHeaders(response.headers)
                        headers(headersString)

                        const reader = response.body.getReader();

                        const contentLength = parseInt(response.headers.get('Content-Length') || '0')
                        let receivedLength = 0;
                        let chunks: BlobPart[] = []

                        reader.read().then(function processText({ done, value }): any {

                            if (done) {
                                let blob = new Blob(chunks, { type: response.headers.get('Content-Type') || '' });
                                load(blob)
                                return;
                            }

                            if (value) {
                                receivedLength += value.length;
                                progress(true, receivedLength, contentLength);
                                chunks.push(value);
                            }

                            return reader.read().then(processText).catch(e => { error(e) });

                        }).catch(e => { error(e) })

                    } else {
                        error('Error Loading Upload');
                    }
                })
                .catch(e => error(e))

            return {
                abort: () => {
                    controller.abort()
                    abort();
                },
            };

        },
    } as FilePondServerConfigProps['server']
}

export const GuidelineDocumentUpload: React.FC<AssetUploadProps> = ({ id, value, label, onChange, error, required, description, disabled, classNames, allowMultiple = false }) => {

    const auth = useAppState(state => state.auth)
    const profile = useAppState(state => state.profile)
    const createDocument = useActions().documents.createItem
    const getDocument = useActions().documents.getItem

    const [files, setFiles] = React.useState<any[]>([])
    const [documentName, setDocumentName] = React.useState<string | null>(null)

    const serverConfig = generateServerConfig(auth, profile)

    const initialValueSet = React.useRef(false)

    React.useEffect(() => {

        const getItem = async () => {
            
            if (!initialValueSet.current) {

                if (typeof value === 'string' && value.length) {
    
                    const document = await getDocument({
                        id: value
                    })
                    console.log('document', document);
    
                    if (document) {

                        console.log('document.url', document.url);

                        setFiles([
                            {
                                source: document.url,
                                options: {
                                    type: 'local'
                                }
                            }
                        ])

                        console.log('value', value);
                        initialValueSet.current = true;

                    }
                }
            }

        }

        getItem()

        return () => {
            initialValueSet.current = false
        }
        
    }, [value, allowMultiple])

    const onUploaded = async (err: FilePondErrorDescription | null, file: FilePondFile) => {
        if (err) {
            console.error('GuidelineDocumentUpload: upload error', err);
            return;
        }

        if (file.serverId) {
            const filename = file.serverId.split('/').pop() || file.filename

            const result = await createDocument({
                url: file.serverId,
                filename,
                size: file.fileSize.toString(),
                filetype: file.fileExtension,
                type: Document_Type_Enum.Document,
                role: Document_Role_Enum.Guideline,
            })

            if (result?.id) {
                onChange(result.id)
                setDocumentName(filename)
            }
        }
    }

    const onUpdatedFiles = (updatedFiles: FilePondFile[]) => {
        const fileList = updatedFiles.map((fileItem) => fileItem.file)
        setFiles(fileList)
        if (!fileList.length) {
            onChange(null)
            setDocumentName(null)
        }
    }

    return (
        <InputWrapper
            id={id}
            label={label}
            description={description}
            error={error}
            required={required}
            classNames={classNames}
            styles={{
                root: {
                    marginBottom: 'var(--mantine-spacing-md)'
                },
                error: {
                    fontSize: 'var(--mantine-font-size-xs)',
                    lineHeight: `var(--mantine-font-size-xs)`,
                    marginTop: 1
                }
            }}
        >
            {documentName && (
                <Text size="sm" c="dimmed" mb={4}>{documentName}</Text>
            )}
            <FilePond
                name="file"
                required={required}
                disabled={disabled}
                allowMultiple={false}
                maxFiles={1}
                server={serverConfig}
                onprocessfile={onUploaded}
                onupdatefiles={onUpdatedFiles}
                files={files}
                allowImagePreview={true}
                imagePreviewTransparencyIndicator="grid"
                labelIdle='<span class="filepond--label-action-desktop">Drag & Drop your document or <span class="filepond--label-action">Browse</span></span> <span class="filepond--label-action-mobile">Select from Device</span></span>'
            />
        </InputWrapper>
    )
}
